import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./FloorManagement.module.css";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../../services/api";

type BackendFloor = {
  id: number;
  number: number;
  name: { ru: string; en?: string };
  mapUrl: string;
  createdAt: string;
  updatedAt: string;
};

export default function FloorManagement() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const modalContentRef = useRef<HTMLDivElement>(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<BackendFloor | null>(null);
  const [number, setNumber] = useState<string>("");
  const [nameRu, setNameRu] = useState<string>("");
  const [nameEn, setNameEn] = useState<string>("");
  const [mapUrl, setMapUrl] = useState<string>("");
  const [formError, setFormError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: floors, isLoading } = useQuery({
    queryKey: ["floors"],
    queryFn: async () => {
      const raw = await apiService.getFloors();
      // raw is backend shape; coerce as any
      return (raw as unknown as BackendFloor[]).sort(
        (a, b) => a.number - b.number
      );
    },
  });

  useEffect(() => {
    if (editing) {
      setNumber(String(editing.number));
      setNameRu(editing.name?.ru || "");
      setNameEn(editing.name?.en || "");
      setMapUrl(editing.mapUrl || "");
    } else {
      setNumber("");
      setNameRu("");
      setNameEn("");
      setMapUrl("");
    }
  }, [editing]);

  const closeForm = () => {
    setIsFormOpen(false);
    setEditing(null);
    setNumber("");
    setNameRu("");
    setNameEn("");
    setMapUrl("");
    setFormError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const openCreate = () => {
    setEditing(null);
    setNumber("");
    setNameRu("");
    setNameEn("");
    setMapUrl("");
    setFormError("");
    createMutation.reset();
    updateMutation.reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setIsFormOpen(true);
  };

  const openEdit = (floor: BackendFloor) => {
    setEditing(floor);
    setFormError("");
    createMutation.reset();
    updateMutation.reset();
    setIsFormOpen(true);
  };

  const createMutation = useMutation({
    mutationFn: async (payload: {
      number: number;
      name: { ru: string; en?: string };
      mapUrl: string;
    }) => apiService.createFloor(payload as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["floors"] });
      setFormError("");
      closeForm();
    },
    onError: (error: any) => {
      const validationErrors = error?.response?.data?.errors;
      if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
        const errorMessages = validationErrors.map((err: any) => {
          const fieldName = err.param || "Поле";
          const message = err.msg || "Ошибка валидации";
          return `• ${fieldName}: ${message}`;
        });
        setFormError(`Ошибки валидации:\n${errorMessages.join("\n")}`);
      } else {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Не удалось создать этаж";
        setFormError(errorMessage);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (vars: {
      id: number;
      data: Partial<{
        number: number;
        name: { ru: string; en?: string };
        mapUrl: string;
      }>;
    }) => apiService.updateFloor(String(vars.id), vars.data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["floors"] });
      setFormError("");
      closeForm();
    },
    onError: (error: any) => {
      const validationErrors = error?.response?.data?.errors;
      if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
        const errorMessages = validationErrors.map((err: any) => {
          const fieldName = err.param || "Поле";
          const message = err.msg || "Ошибка валидации";
          return `• ${fieldName}: ${message}`;
        });
        setFormError(`Ошибки валидации:\n${errorMessages.join("\n")}`);
      } else {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Не удалось обновить этаж";
        setFormError(errorMessage);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => apiService.deleteFloor(String(id)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["floors"] }),
    onError: (error: any) => {
      const message =
        error.response?.data?.message || "Ошибка при удалении этажа";
      alert(message);
    },
  });

  const onFileChange: React.ChangeEventHandler<HTMLInputElement> = async (
    e
  ) => {
    const f = e.target.files?.[0];
    if (!f) return;

    // Validate file type - only images allowed
    const allowedImageTypes = /image\/(jpeg|jpg|png|gif|svg|webp|avif|bmp)/i;
    const allowedExtensions = /\.(jpeg|jpg|png|gif|svg|webp|avif|bmp)$/i;
    
    if (!allowedImageTypes.test(f.type) && !allowedExtensions.test(f.name)) {
      alert("Разрешены только файлы изображений (jpeg, jpg, png, gif, svg, webp, avif, bmp)!");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    try {
      const uploaded = await apiService.uploadFloorMap(f);
      setMapUrl(uploaded.url);
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Ошибка при загрузке изображения";
      alert(message);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const onSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    setFormError("");

    const validationMessages: string[] = [];

    // Validate name (RU) - cannot be empty or only spaces
    const trimmedNameRu = nameRu.trim();
    if (!trimmedNameRu) {
      validationMessages.push("• Название (RU): не может быть пустым или состоять только из пробелов");
    }

    // Validate mapUrl - required for creation
    if (!editing && !mapUrl) {
      validationMessages.push("• Изображение карты этажа: необходимо загрузить");
    }

    if (validationMessages.length > 0) {
      setFormError(`Ошибки валидации:\n${validationMessages.join("\n")}`);
      return;
    }

    const payload = {
      number: parseInt(number, 10),
      name: { ru: trimmedNameRu, ...(nameEn ? { en: nameEn } : {}) },
      mapUrl,
    };
    if (editing) {
      updateMutation.mutate({ id: editing.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const mediaBase = useMemo(() => process.env.REACT_APP_MEDIA_PATH, []);
  const fullUrl = mapUrl
    ? mapUrl.startsWith("http")
      ? mapUrl
      : `${mediaBase}${mapUrl.startsWith("/") ? "" : "/"}${mapUrl}`
    : "";

  useEffect(() => {
    if (isFormOpen && modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [isFormOpen]);

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.logo}>Управление картой этажей</div>
        <button
          className={styles.backButton}
          onClick={() => navigate("/admin")}
        >
          Назад
        </button>
      </header>
      <main className={styles.mainContent}>
        <h1 className={styles.pageTitle}>Этажи</h1>
        
        <button className={styles.addButton} onClick={openCreate}>
          + Добавить этаж
        </button>

        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>Список этажей</h2>
          {isLoading ? (
            <div>Загрузка...</div>
          ) : (
            <table className={styles.floorsTable}>
              <thead>
                <tr>
                  <th className={styles.th}>Этаж</th>
                  <th className={styles.th}>Название (RU/EN)</th>
                  <th className={styles.th}>Карта</th>
                  <th className={styles.th}>Действия</th>
                </tr>
              </thead>
              <tbody>
                {(floors || []).map((f) => {
                  const url = f.mapUrl?.startsWith("http")
                    ? f.mapUrl
                    : `${mediaBase}${f.mapUrl?.startsWith("/") ? "" : "/"}${
                        f.mapUrl
                      }`;
                  return (
                    <tr key={f.id}>
                      <td className={styles.td}>{f.number}</td>
                      <td className={styles.td}>
                        <div>{f.name?.ru || "—"}</div>
                        <div style={{ color: "#777" }}>{f.name?.en || "—"}</div>
                      </td>
                      <td className={styles.td}>
                        {url ? (
                          <img className={styles.preview} src={url} alt="map" />
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className={styles.td}>
                        <button
                          className={styles.actionButton}
                          onClick={() => openEdit(f)}
                        >
                          Редактировать
                        </button>
                        <button
                          className={`${styles.actionButton} ${styles.actionDanger}`}
                          onClick={() => {
                            if (window.confirm("Удалить этаж?"))
                              deleteMutation.mutate(f.id);
                          }}
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div
          className={`${styles.modal} ${isFormOpen ? styles.modalOpen : ""}`}
        >
          <div ref={modalContentRef} className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>
              {editing ? "Редактирование этажа" : "Добавить этаж"}
            </h2>
            <form onSubmit={onSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Номер этажа</label>
                <input
                  className={styles.input}
                  type="number"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Название (RU)</label>
                <input
                  className={styles.input}
                  type="text"
                  value={nameRu}
                  onChange={(e) => setNameRu(e.target.value)}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Название (EN)</label>
                <input
                  className={styles.input}
                  type="text"
                  value={nameEn}
                  onChange={(e) => setNameEn(e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Изображение карты этажа</label>
                <input
                  ref={fileInputRef}
                  className={styles.fileInput}
                  type="file"
                  accept="image/*"
                  onChange={onFileChange}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Превью</label>
                {fullUrl ? (
                  <img className={styles.preview} src={fullUrl} alt="floor-map" />
                ) : (
                  <span>Нет изображения</span>
                )}
              </div>
              {formError && (
                <div
                  className={styles.errorMessage}
                  style={{ whiteSpace: "pre-line" }}
                >
                  {formError}
                </div>
              )}
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.actionWarning}`}
                  onClick={closeForm}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className={styles.actionButton}
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {editing ? "Сохранить" : "Создать"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
